<template>
  <div class="flex gap-1 align-items-start align-items-center">
    <slot name="upload-button">
      <div class="flex flex-column justify-content-center align-items-center">
        <FMFileUpload
          ref="fileUpload"
          v-model:upload-in-progress="uploadInProgress"
          style="display: none"
          :prevent-copy-paste="filesTableVisible || preventCopyPaste"
          @file-created="filesUploaded"
        />
        <LISAButton
          style="height: 4em"
          :text="true"
          :disabled="disabledComputed"
          data-testid="file-inline-upload-button"
          @click="filesTableVisible = true"
        >
          <i :class="`fas ${uploadInProgress ? 'fa-refresh fa-spin' : 'fa-plus'}`" />
          <div class="mt-2">{{ t("components.TaskModal.attachments") }}</div>
        </LISAButton>
      </div>
      <div class="flex gap-1 align-items-start overflow-x-auto p-1">
        <div
          v-for="file in allFiles"
          :key="file.key"
          class="inline-file-preview"
          :class="{ 'inline-file-preview--formatted': formattedThumbnails }"
        >
          <FMFilePreview
            :file="file"
            :data-testid="file.name"
            :hover-and-shadow-effect-visible="true"
            :hide-without-thumbnail="false"
            :size-x="formattedThumbnails ? '6.125em' : '3.5em'"
            size-y="3.5em"
            :class="{
              'inline-file-preview__thumbnail--formatted': formattedThumbnails,
            }"
            @click.stop="previewFile(file)"
          />
          <button
            v-if="removableThumbnails && !disabledComputed"
            type="button"
            class="inline-file-preview__remove"
            :aria-label="t('global.delete')"
            :title="t('global.delete')"
            :data-testid="`remove-${file.name}`"
            @click.stop="removeFile(file)"
          >
            <i class="fas fa-times" />
          </button>
        </div>
      </div>
    </slot>
    <LModal
      v-model:visible="filesTableVisible"
      size="xl"
      :ok-disabled="uploadInProgressModal"
      :no-close-on-backdrop="uploadInProgressModal"
      :title="t('components.TaskModal.attachments')"
      :submit="confirmAttachments"
    >
      <div style="height: 65vh">
        <FMFolderTableComponent
          v-model:upload-in-progress="uploadInProgressModal"
          :file-select-mode="false"
          :files-and-folders="allFiles"
          :loading="loading"
          :reserve-space-for-breadcrumb="false"
          :folder-id="tempFolderId"
          @file-created="filesUploaded"
          @file-deleted="fileDeleted"
        />
      </div>
    </LModal>
    <FMFileViewerModal
      v-model:visible="selectedFile.visible"
      v-model:active-index="activeIndexGallery"
      :files="allFiles"
    />
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import LISAButton from "@/components/shared/primevue/LISAButton.vue";
import { computed, inject, nextTick, onMounted, ref } from "vue";
import FMFilePreview from "./FMFilePreview.vue";
import FMFileUpload from "./FMFileUpload.vue";
import { useFolderTableContentFormat } from "./FMFileUploadHelper";
import FMFileViewerModal from "./FMFileViewerModal.vue";
import LModal from "@/components/LModal.vue";
import FMFolderTableComponent from "./FMFolderTableComponent.vue";
import type { FolderTableContent } from "../helper/FMFilePreviewHelper";
import {
  FMFolderType,
  useFMFileForFolderTableQuery,
  useFMSpecialFolderIdQuery,
  type FMFolderContentFileFragment,
} from "@/graphql";
import {
  claimAutomaticScreenshotRequest,
  EnableAutomaticScreenshotInjectionKey,
  releaseAutomaticScreenshotRequest,
  useScreenshot,
} from "@/functions/ScreenshotHelper";
import { InputFrameInjectionKey } from "@/components/shared/form/InputFrameMode.ts";

const props = defineProps({
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  automaticScreenshot: {
    type: Boolean,
    required: false,
    default: false,
  },
  formattedThumbnails: {
    type: Boolean,
    required: false,
    default: false,
  },
  removableThumbnails: {
    type: Boolean,
    required: false,
    default: false,
  },
  preventCopyPaste: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const inputFrameProps = inject(
  InputFrameInjectionKey,
  computed(() => ({
    disable: false,
    submit: () => {},
  })),
);
const formDisabled = computed(() => inputFrameProps.value.disable);

const disabledComputed = computed(() => props.disabled || formDisabled.value);

const fileIds = defineModel<number[]>("files-ids", {
  type: Array as () => number[],
  required: true,
  default: () => [],
});

const uploadInProgress = ref(false);
const uploadInProgressModal = ref(false);
const fileUpload = ref<InstanceType<typeof FMFileUpload>>();

const screenShotOptions = inject(
  EnableAutomaticScreenshotInjectionKey,
  computed(() => undefined),
);

const { getBlob } = useScreenshot();

onMounted(async () => {
  await nextTick();
  if (props.automaticScreenshot && screenShotOptions?.value?.FileComponent.enable) {
    const screenshotFileIds = fileIds.value;
    if (screenshotFileIds.length > 0 || !claimAutomaticScreenshotRequest(screenshotFileIds)) {
      return;
    }

    try {
      // Automatic attachments are always scoped to the injected tile container.
      const screenshot = await getBlob();

      if (screenshot == null) {
        releaseAutomaticScreenshotRequest(screenshotFileIds);
        return;
      }

      if (fileIds.value !== screenshotFileIds) {
        releaseAutomaticScreenshotRequest(screenshotFileIds);
        return;
      }

      /**
       * FYI: image.png keeps the upload component's generated screenshot naming behavior.
       */
      await fileUpload.value?.addFile(new File([screenshot], "image.png", { type: "image/png" }));
    } catch {
      releaseAutomaticScreenshotRequest(screenshotFileIds);
      // The screenshot is optional; keep the attachment UI usable if capture/upload fails.
    }
  }
});

const { result: resultTempFolder, loading: loadingTempFolder } = useFMSpecialFolderIdQuery(
  {
    types: FMFolderType.TEMPORARY,
  },
  {
    fetchPolicy: "cache-first",
  },
);

const filesInternally = ref<FolderTableContent[]>([]);

const filesToQuery = computed(() =>
  fileIds.value.filter((id) => filesInternally.value.every((file) => file.fileId !== id)),
);

const { result: fileResult, loading: loadingFiles } = useFMFileForFolderTableQuery(
  computed(() => ({
    fileIds: filesToQuery.value,
  })),
  computed(() => ({
    fetchPolicy: "cache-first",
    enabled: filesToQuery.value.length > 0,
  })),
);
const tempFolderId = computed(() => resultTempFolder.value?.FMFolders?.[0]?.id ?? 1);

const allFiles = computed(() => {
  const attachedFileIds = new Set(fileIds.value);

  return [...filesInternally.value, ...(fileResult.value?.FMFiles.map(getFileFormat) ?? [])]
    .filter((file) => file.fileId != null && attachedFileIds.has(file.fileId))
    .sort((a, b) => b.createdAtISO.localeCompare(a.createdAtISO));
});

const selectedFile = ref({
  visible: false,
  file: undefined as (typeof filesInternally.value)[number] | undefined,
});

const activeIndexGallery = ref(0);

function previewFile(file: (typeof allFiles.value)[number]) {
  selectedFile.value.file = file;
  activeIndexGallery.value = allFiles.value.findIndex((f) => f.key === file.key);
  selectedFile.value.visible = true;
}

const { getFileFormat } = useFolderTableContentFormat(true);

function filesUploaded(file: FMFolderContentFileFragment) {
  filesInternally.value.unshift(getFileFormat(file));
  fileIds.value.push(file.id);
}

function fileDeleted(fileId: number) {
  const index = filesInternally.value.findIndex((f) => f.fileId === fileId);
  if (index !== -1) {
    filesInternally.value.splice(index, 1);
  }
  const idIndex = fileIds.value.findIndex((id) => id === fileId);
  if (idIndex !== -1) {
    fileIds.value.splice(idIndex, 1);
  }
}

function removeFile(file: (typeof allFiles.value)[number]) {
  if (file.fileId != null) {
    fileDeleted(file.fileId);
  }
}

const filesTableVisible = ref(false);

const loading = computed(() => loadingTempFolder.value || loadingFiles.value);

async function confirmAttachments() {
  return { success: true } as const;
}

const { t } = useTranslation();
</script>

<style lang="scss" scoped>
.inline-file-preview {
  position: relative;
  flex: 0 0 3.5em;
  height: 3.5em;
}

.inline-file-preview--formatted {
  aspect-ratio: 1.148698355 / 1;
}

.inline-file-preview__thumbnail--formatted {
  width: 100%;
  padding: 0 !important;
  border-radius: var(--p-border-radius-md);
}

.inline-file-preview__thumbnail--formatted :deep(img) {
  width: auto;
  height: auto;
  border-radius: var(--p-border-radius-md);
}

.inline-file-preview__remove {
  position: absolute;
  z-index: 1;
  top: 0.1rem;
  right: 0.1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: var(--p-surface-0);
  background: var(--p-red-600);
  font-size: 0.625rem;
  cursor: pointer;
}

.inline-file-preview__remove:hover,
.inline-file-preview__remove:focus-visible {
  background: var(--p-red-700);
}

.inline-file-preview__remove:focus-visible {
  outline: 2px solid var(--p-primary-color);
  outline-offset: 1px;
}
</style>
